import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export interface DateAvailability {
  date: string;
  dayOfWeek: number;
  isBusinessDay: boolean;
  isClosed: boolean;
  closedReason?: string;
  openTime?: string;
  totalCapacityMinutes: number;
  usedCapacityMinutes: number;
  availableCapacityMinutes: number;
  bookingCount: number;
  availableMechanicsCount: number;
  /** Skill-aware capacity (epic #1140): present only when serviceIds is supplied. */
  qualifiedMechanicsCount?: number;
  qualifiedCapacityMinutes?: number;
  totalLiftsCount: number;
  liftsUsedOnDayCount: number;
  mechanicsAvailability?: Record<string, { available: boolean; bookedMinutes: number; availableMinutes: number; name: string }>;
  allMechanicsAvailable?: boolean;
  liftAvailable?: boolean;
  availabilityLevel: 'full' | 'high' | 'medium' | 'low' | 'none';
}

export interface SchedulingAvailabilityParams {
  startDate: string;
  endDate: string;
  estimatedDurationHours?: number;
  mechanicIds?: string[];
  /** Selected service IDs — enables skill-aware qualified capacity. */
  serviceIds?: string[];
  liftType?: string;
}

export interface AvailabilitySlot {
  /** Slot start as "HH:mm" workshop wallclock. */
  start: string;
  busyCount: number;
  availableMechanics: number;
  /** The requested duration fits starting here (ends by closing, capacity free). */
  fits: boolean;
  /** Today's already-elapsed slot — not offerable. */
  isPast: boolean;
}

export interface DaySlots {
  date: string;
  isClosed: boolean;
  closedReason?: string;
  openTime?: string;
  closeTime?: string;
  slotMinutes: number;
  /** Mechanics qualified for the selected services AND at work that day. */
  totalMechanics: number;
  /**
   * Mechanics at work that day before the skill filter (#1932). Together with
   * `totalMechanics` it separates "nobody holds the required skills" from
   * "nobody is at work" — both arrive as zero qualified capacity, and only the
   * former is an ordinary, selectable day.
   */
  mechanicsAtWork?: number;
  slots: AvailabilitySlot[];
}

export interface SchedulingSlotsParams {
  date: string;
  estimatedDurationHours?: number;
  /** Selected service IDs — enables skill-aware qualified capacity. */
  serviceIds?: string[];
}

/** Slot-level availability for one day (#1142) — the time picker's slot grid. */
export function useSchedulingSlots(params: SchedulingSlotsParams | null) {
  return useQuery({
    queryKey: queryKeys.bookings.schedulingSlots(params ? { ...params } : {}),
    queryFn: () => {
      const queryParams: Record<string, string | number> = { date: params?.date ?? '' };
      if (params?.estimatedDurationHours && params.estimatedDurationHours > 0) {
        queryParams['estimatedDurationHours'] = params.estimatedDurationHours;
      }
      if (params?.serviceIds && params.serviceIds.length > 0) {
        queryParams['serviceIds'] = params.serviceIds.join(',');
      }
      return api.get<DaySlots>('/bookings/scheduling-availability/slots', { params: queryParams });
    },
    enabled: params !== null && params.date !== '',
    staleTime: 30_000,
  });
}

export function useSchedulingAvailability(params: SchedulingAvailabilityParams | null) {
  return useQuery({
    queryKey: queryKeys.bookings.schedulingAvailability(
      params ? { ...params } : {},
    ),
    queryFn: () => {
      const queryParams: Record<string, string | number> = {};
      if (params) {
        queryParams['startDate'] = params.startDate;
        queryParams['endDate'] = params.endDate;
        if (params.estimatedDurationHours && params.estimatedDurationHours >= 15) queryParams['estimatedDurationHours'] = params.estimatedDurationHours;
        if (params.mechanicIds && params.mechanicIds.length > 0) queryParams['mechanicIds'] = params.mechanicIds.join(',');
        if (params.serviceIds && params.serviceIds.length > 0) queryParams['serviceIds'] = params.serviceIds.join(',');
        if (params.liftType) queryParams['liftType'] = params.liftType;
      }
      return api.get<{ dates: DateAvailability[] }>('/bookings/scheduling-availability', {
        params: queryParams,
      });
    },
    enabled: params !== null,
    staleTime: 30_000,
    select: (data) => data.dates,
  });
}
